import { SUPPORTED_LANGUAGES, SupportedLanguage, useTranslation } from '@/hooks/useTranslation'
import { ExpandMore, Language as LanguageIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import notificationApi from '@/api/admin/notificationApi'
import useUser from '@/features/auth/hooks/useUser'
import { User } from '@/types/user'
import { getStoredToken, isTokenExpired } from '@/utils/jwtUtils'

interface LanguageSwitcherProps {
  variant?: 'button' | 'icon' | 'minimal'
  size?: 'small' | 'medium' | 'large'
  showFlag?: boolean
  showText?: boolean
  sx?: SxProps<Theme>
  onLanguageChange?: (language: SupportedLanguage) => void
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'button',
  size = 'medium',
  showFlag = true,
  showText = true,
  sx,
  onLanguageChange,
}) => {
  const { currentLanguage, languageInfo, switchLanguage, t } = useTranslation()

  const isTokenValid = useMemo(() => {
    const token = getStoredToken()
    if (!token) return false
    return !isTokenExpired(token)
  }, [])

  const { data: user } = useUser<User>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLanguageSelect = async (language: SupportedLanguage) => {
    try {
      if (isTokenValid && user?.email) {
        try {
          await notificationApi.updateSubscriptionInfo({
            email: user.email,
            language: language,
          })
        } catch (error) {
          console.error('Failed to update notification subscription info:', error)
        }
      }

      await switchLanguage(language)
      onLanguageChange?.(language)

      handleClose()

      window.location.reload()
    } catch (error) {
      console.error('Failed to switch language:', error)
    }
  }

  const renderTrigger = () => {
    const commonProps = {
      onClick: handleClick,
      'aria-controls': open ? 'language-menu' : undefined,
      'aria-haspopup': 'true' as const,
      'aria-expanded': open,
      sx: sx,
    }

    switch (variant) {
      case 'icon':
        return (
          <Tooltip title={t('changeLanguage')}>
            <IconButton {...commonProps} size={size}>
              <LanguageIcon />
            </IconButton>
          </Tooltip>
        )

      case 'minimal':
        return (
          <Button
            {...commonProps}
            variant="text"
            size={size}
            startIcon={
              showFlag ? <span>{languageInfo.flag}</span> : <LanguageIcon />
            }
            endIcon={<ExpandMore />}
            sx={{
              minWidth: 'auto',
              textTransform: 'none',
              color: 'text.secondary',
              ...sx,
            }}
          >
            {showText && (
              <Typography variant="body2" component="span">
                {languageInfo.name}
              </Typography>
            )}
          </Button>
        )

      default: // button
        return (
          <Button
            {...commonProps}
            variant="outlined"
            size={size}
            startIcon={
              showFlag ? (
                <span style={{ fontSize: '1.2em' }}>{languageInfo.flag}</span>
              ) : (
                <LanguageIcon />
              )
            }
            endIcon={<ExpandMore />}
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
              ...sx,
            }}
          >
            {showText ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ lineHeight: 1 }}
                >
                  {languageInfo.name}
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ color: 'text.secondary', lineHeight: 1 }}
                >
                  {languageInfo.name}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                component="span"
                sx={{ lineHeight: 1 }}
              >
                {t('changeLanguage')}
              </Typography>
            )}
          </Button>
        )
    }
  }

  return (
    <>
      {renderTrigger()}
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => (
          <MenuItem
            key={code}
            onClick={() => handleLanguageSelect(code as SupportedLanguage)}
            selected={code === currentLanguage}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <span style={{ fontSize: '1.5em' }}>{info.flag}</span>
            </ListItemIcon>

            <ListItemText
              primary={info.name}
              // secondary={code.toUpperCase()}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: code === currentLanguage ? 600 : 400,
                color:
                  code === currentLanguage ? 'text.primary' : 'text.inherit',
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'text.secondary',
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default LanguageSwitcher
